// 引入 React 与常用 Hook。
import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
// 引入国际化 Hook。
import { useTranslations } from 'next-intl';
// 引入通用图标组件。
import CommonIcon from '@/components/CommonIcon';
// 引入 Toast 组件。
import Toast from '@/components/Toast';
// 引入阿里云验证码组件。
import AliyunCaptcha, { AliyunCaptchaInstance } from '@/components/AliyunCaptcha';
import {
  getUserCreditsSummary,
  getUserCreditsLogs,
  getUserCreditsExchangeCaptchaConfig,
  applyUserCreditsExchange,
} from '@/api/user';
import { isMobile } from '@/utils/env';
import dayjs from 'dayjs';
import Styles from './index.module.scss';
// 引入全局上下文。
import { StoreContext, namespace } from '@/context';
// 引入 SLS 上报能力。
import useSls, { MetricTypes } from '@/hooks/useSls';

// Import Icons using relative path to avoid babel-plugin-inline-react-svg alias issue
import ErrorBadgeIcon from '../../../../assets/icon/account/error-badge.svg';
import GoDetailsIcon from '../../../../assets/icon/account/go-details.svg';

interface IPointsExchangeModalProps {
  visible: boolean;
  onClose: () => void;
  onPointsUpdate?: (points: number) => void;
}

interface IPointsSummary {
  availableCredits: number;
  pendingCredits: number;
  exchangedCredits: number;
  
}

interface ICaptchaConfig {
  enabled: boolean;
  prefix: string;
  sceneH5: string;
  scenePc: string;
  sceneType: string;
}

type PointsRecord = {
  title: string;
  date: string;
  delta: number; // Positive for increase, negative for decrease
  balance: number; // Balance after change
  status?: string;
};

// 兑换规则常量。
const MIN_EXCHANGE_POINTS = 300;
const EXCHANGE_STEP = 30;
const PAGE_SIZE = 20;

// 积分兑换弹窗组件。
const PointsExchangeModal: React.FC<IPointsExchangeModalProps> = ({ visible, onClose, onPointsUpdate }) => {
  // 国际化函数。
  const t = useTranslations();
  // 获取全局埋点与账号信息。
  const { state } = useContext(StoreContext);
  const { sensors } = state[namespace.app];
  const { track, TRACK_EVENTS } = sensors || {};
  const { reportSlsData } = useSls();
  const account = (state as any)?.[namespace.app]?.account;
  // Mock Data
  const [availablePoints, setAvailablePoints] = useState(0);
  const [pendingPoints, setPendingPoints] = useState(0);
  const [exchangedPoints, setExchangedPoints] = useState(0);

  const [records, setRecords] = useState<PointsRecord[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isExchangeInputFocused, setIsExchangeInputFocused] = useState(false);
  const [isScrollAtTop, setIsScrollAtTop] = useState(true);
  const [hasScrollbar, setHasScrollbar] = useState(false);

  const [exchangeInput, setExchangeInput] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const [successToastVisible, setSuccessToastVisible] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [captchaVerifyParam, setCaptchaVerifyParam] = useState("");
  const [captchaConfig, setCaptchaConfig] = useState<ICaptchaConfig | null>(null);
  const captchaInstance = useRef<AliyunCaptchaInstance | null>(null);
  // 更新积分汇总到页面与外层。
  const updatePointsSummary = (summary: IPointsSummary) => {
    const latestAvailablePoints = summary.availableCredits || 0;
    setAvailablePoints(latestAvailablePoints);
    setPendingPoints(summary.pendingCredits || 0);
    setExchangedPoints(summary.exchangedCredits || 0);
    onPointsUpdate?.(latestAvailablePoints);
  };
  // 拉取积分记录分页数据。
  const fetchRecords = async (pageNum: number) => {
    if (loadingRecords) return;
    setLoadingRecords(true);
    try {
      const res = await getUserCreditsLogs({
        page: pageNum,
        page_size: PAGE_SIZE,
      });

      const list = Array.isArray(res?.list) ? res.list : [];
      if (list.length > 0) {
        const newRecords: PointsRecord[] = list.map((item) => ({
          title: item.type,
          date: dayjs(item.time * 1000).format("YYYY-MM-DD"),
          delta: item.changeCredits,
          balance: item.availableCredits,
          status: item.status,
        }));

        setRecords(prev => pageNum === 1 ? newRecords : [...prev, ...newRecords]);
        setHasMore(newRecords.length >= PAGE_SIZE);
        setPage(pageNum);
      } else {
        setHasMore(false);
      }
    } catch (err: any) {
      console.error("Fetch records failed:", err);
      // Don't toast on initial load error to avoid multiple toasts if both APIs fail? 
      // Or toast is fine.
    } finally {
      setLoadingRecords(false);
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    // 弹层打开时重置输入状态并预取数据。
    if (visible) {
      setExchangeInput("");
      setErrorMsg("");
      setIsExchangeInputFocused(false);
      setIsScrollAtTop(true);
      setHasScrollbar(false);
      setConfirmVisible(false);
      setSuccessToastVisible(false);
      setIsCaptchaVerified(false);
      setCaptchaVerifyParam("");

      // Fetch points summary
      getUserCreditsSummary().then((summary) => {
        updatePointsSummary(summary);
      }).catch((err: any) => {
        console.error("Fetch points summary failed:", err);
        const errorMsg = err?.message || t('points_exchange_fetch_summary_failed');
        Toast.show({ content: errorMsg });
      });

      // Fetch first page of records
      fetchRecords(1);

      // Fetch Captcha Config
      getUserCreditsExchangeCaptchaConfig().then((config) => {
        if (config) {
          setCaptchaConfig({
            enabled: config.enabled,
            prefix: config.prefix,
            sceneH5: config.sceneH5,
            scenePc: config.scenePc,
            sceneType: config.sceneType,
          });
        }
      }).catch((err: any) => {
        console.error("Fetch captcha config failed:", err);
      });
    }
  }, [visible]);

  // 列表滚动监听：更新滚动态并触底加载更多。
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const { scrollTop, clientHeight, scrollHeight } = target;
    setIsScrollAtTop(scrollTop <= 0);
    setHasScrollbar(scrollHeight > clientHeight);
    // Load more when scrolled to bottom (within 50px)
    if (scrollHeight - scrollTop - clientHeight < 50 && hasMore && !loadingRecords) {
      fetchRecords(page + 1);
    }
  };

  // 监听容器尺寸变化，更新滚动条状态。
  useEffect(() => {
    if (!visible) return;
    const checkScrollbar = () => {
      const container = scrollContainerRef.current;
      if (!container) return;
      setHasScrollbar(container.scrollHeight > container.clientHeight);
      setIsScrollAtTop(container.scrollTop <= 0);
    };
    const frameId = window.requestAnimationFrame(checkScrollbar);
    window.addEventListener('resize', checkScrollbar);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', checkScrollbar);
    };
  }, [visible, records.length, availablePoints, pendingPoints, exchangedPoints, errorMsg, successToastVisible]);

  // Reset captcha when confirm dialog closes
  useEffect(() => {
    if (!confirmVisible) {
      setIsCaptchaVerified(false);
      setCaptchaVerifyParam("");
    }
  }, [confirmVisible]);

  // 解析输入兑换积分为整数。
  const exchangeAmount = useMemo(() => {
    if (!exchangeInput) return 0;
    const n = Number(exchangeInput);
    return Number.isFinite(n) ? Math.floor(n) : 0;
  }, [exchangeInput]);

  // 成功提示自动关闭。
  useEffect(() => {
    if (!successToastVisible) return;
    const t = window.setTimeout(() => setSuccessToastVisible(false), 2000);
    return () => window.clearTimeout(t);
  }, [successToastVisible]);

  // 输入框变更处理。
  const onChangeExchangeInput = (val: string) => {
    const next = (val || "").replace(/[^\d]/g, "");
    setExchangeInput(next);
    
    if (!next) {
      if (errorMsg) setErrorMsg("");
      return;
    }
    const n = Number(next);
    const amount = Number.isFinite(n) ? Math.floor(n) : 0;
    if (amount > availablePoints) {
      setErrorMsg(t('points_exchange_error_exceed_available'));
      return;
    }
    if (errorMsg) setErrorMsg("");
  };

  // 提交兑换前校验并打开确认弹层。
  const onSubmitExchange = () => {
    const reportPointsButtonClick = (isSuccess: boolean, failReason: string) => {
      track?.(TRACK_EVENTS?.[namespace.home]?.pointsButtonClick?.name, {
        page_name: 'MyProfilePage',
        platform_type: 'pc',
        content_type: exchangeAmount,
        button_name: t('points_exchange_button_exchange'),
        is_success: isSuccess,
        fail_reason: failReason,
      }, namespace.home);
    };
    if (!exchangeInput || exchangeAmount <= 0) {
      const failReason = t('points_exchange_error_input_amount');
      setErrorMsg(failReason);
      reportPointsButtonClick(false, failReason);
      return;
    }
    if (exchangeAmount < MIN_EXCHANGE_POINTS) {
      const failReason = t('points_exchange_error_minimum', { min: MIN_EXCHANGE_POINTS });
      setErrorMsg(failReason);
      reportPointsButtonClick(false, failReason);
      return;
    }
    if (exchangeAmount > availablePoints) {
      const failReason = t('points_exchange_error_exceed_available');
      setErrorMsg(failReason);
      reportPointsButtonClick(false, failReason);
      return;
    }
    if (exchangeAmount % EXCHANGE_STEP !== 0) {
      const failReason = t('points_exchange_error_step', { step: EXCHANGE_STEP });
      setErrorMsg(failReason);
      reportPointsButtonClick(false, failReason);
      return;
    }
    reportPointsButtonClick(true, "");
    setConfirmVisible(true);
  };

  // 验证码滑块样式。
  const captchaSlideStyle = useMemo(() => ({ width: 320, height: 48 }), []);

  // 确认兑换：调用接口并刷新汇总与记录。
  const onConfirmExchange = () => {
    const reportPointsRedemptionButtonClick = (isSuccess: boolean, failReason: string) => {
      track?.(TRACK_EVENTS?.[namespace.home]?.pointsRedemptionButtonClick?.name, {
        page_name: 'MyProfilePage',
        platform_type: 'pc',
        button_name: t('points_exchange_confirm'),
        is_success: isSuccess,
        fail_reason: failReason,
        content_type: exchangeAmount,
      }, namespace.home);
    };

    if (captchaConfig?.enabled && !isCaptchaVerified) {
      const failReason = t('points_exchange_error_verify_first');
      reportPointsRedemptionButtonClick(false, failReason);
      Toast.show({ content: failReason });
      return;
    }
    
    if (confirmSubmitting) return;
    setConfirmSubmitting(true);
    
    // Prepare params
    const params = {
      credits: exchangeAmount,
      ...(captchaConfig?.enabled ? {
        scene: captchaSceneId,
        captcha_verify_param:captchaVerifyParam,
      } : {}),
    };
    const requestStartTime = Date.now();
    const reportExchangeApplySls = (failReason: string, responseCode: string | number) => {
      const duration = Date.now() - requestStartTime;
      const uid = Number((account as any)?.id);
      const isSuccess = Number(responseCode) === 200 ? 1 : 0;
      reportSlsData([
        ['reportMetric', MetricTypes.userCreditsExchangeApplyApiTime, duration],
        [
          'setEnvironment',
          {
            api_path: '/users/credits/exchange/apply',
            api_params: JSON.stringify(params),
            uid: Number.isFinite(uid) ? uid : null,
            is_success: isSuccess,
            fail_reason: failReason,
            terminal_type: 'pc',
            response_code: responseCode,
          },
        ],
      ]);
    };

    applyUserCreditsExchange(params).then(() => {
      
        reportExchangeApplySls('', 200);
        reportPointsRedemptionButtonClick(true, "");
      
        setConfirmSubmitting(false);
        setConfirmVisible(false);
        setSuccessToastVisible(true);
        
        // Reset inputs
        setExchangeInput("");
        setErrorMsg("");
        
        // Refetch summary
        getUserCreditsSummary().then((summary) => {
            updatePointsSummary(summary);
        });
        
        // Refetch records
        fetchRecords(1);
        
    }).catch((err: any) => {
        const msg = err?.message || t('points_exchange_failed');
        const responseCode = err?.status ?? err?.response?.status ?? err?.code ?? -1;
        reportExchangeApplySls(msg, responseCode);
        reportPointsRedemptionButtonClick(false, msg);
        setConfirmSubmitting(false);
        const errorMsg = msg;
        // Handle specific error codes if needed, but Toast is usually enough
        Toast.show({ content: errorMsg });
    }).finally(() => {
        if (captchaConfig?.enabled && captchaInstance.current) {
            captchaInstance.current.refresh();
            setIsCaptchaVerified(false);
            setCaptchaVerifyParam("");
        }
    });
  };

  // 验证按钮点击埋点上报。
  const reportCheckButtonClick = (isSuccess: boolean, failReason: string) => {
    track?.(TRACK_EVENTS?.[namespace.home]?.checkButtonClick?.name, {
      page_name: 'MyProfilePage',
      platform_type: 'pc',
      button_name: t('points_exchange_captcha_verify_pc'),
      is_success: isSuccess,
      fail_reason: failReason,
    }, namespace.home);
  };

  // 验证码通过回调。
  const onCaptchaSuccess = (verifyParam: string) => {
    setIsCaptchaVerified(true);
    setCaptchaVerifyParam(verifyParam);
    reportCheckButtonClick(true, "");
  };

  // 验证码失败回调。
  const onCaptchaFail = () => {
    const failReason = t('points_exchange_verify_failed_retry');
    reportCheckButtonClick(false, failReason);
    Toast.show({ content: failReason });
  };

  // 映射记录状态样式类。
  const getStatusClass = (status: string) => {
    if (status.includes("待审核") || status.includes("审核中"))  return Styles.statusPending;
    if (status.includes("已兑换") || status.includes("兑换成功") || status.includes("成功")) return Styles.statusSuccess;
    if (status.includes("失败")) return Styles.statusFail;
    return "";
  };

  // 按终端选择验证码场景 ID。
  const captchaSceneId = useMemo(() => {
    if (!captchaConfig) return '';
    return isMobile ? captchaConfig.sceneH5 : captchaConfig.scenePc;
  }, [captchaConfig]);

  // 不可见时不渲染。
  if (!visible) return null;

  return (
    <>
      <div className={Styles.mask}>
        <div className={Styles.container}>
          <div className={Styles.closeBtn} onClick={onClose}>
            <CommonIcon icon="my-sb-modal-close" />
          </div>


          <div 
            className={`${Styles.scrollableContent} ${hasScrollbar ? Styles.scrollableWithScrollbar : ''} ${isScrollAtTop ? Styles.scrollbarHidden : ''}`} 
            onScroll={onScroll}
            ref={scrollContainerRef}
          >
            <div className={Styles.modalTitle}>{t('points_exchange_available_points_title')}</div>
            {successToastVisible && (
              <div className={Styles.successToast}>
                <span className={Styles.successToastText}>{t('points_exchange_success_submitted')}</span>
              </div>
            )}

            <div className={Styles.availableValue}>{availablePoints}</div>
            
            <div className={Styles.subStats}>
              <span>{t('points_exchange_pending_label')}{pendingPoints}</span>
              <span>{t('points_exchange_exchanged_label')}{exchangedPoints}</span>
            </div>

            <div className={Styles.exchangeArea}>
              <input
                className={Styles.exchangeInput}
                placeholder={isExchangeInputFocused ? "" : t('points_exchange_error_input_amount')}
                value={exchangeInput}
                onChange={(e) => onChangeExchangeInput(e.target.value)}
                onFocus={() => setIsExchangeInputFocused(true)}
                onBlur={() => setIsExchangeInputFocused(false)}
              />

              {!!errorMsg && (
                <div className={Styles.errorRow}>
                  <ErrorBadgeIcon className={Styles.errorBadge} aria-hidden="true" />
                  <span className={Styles.errorText}>{errorMsg}</span>
                </div>
              )}

              <button
                className={Styles.exchangeBtn}
                onClick={onSubmitExchange}
                disabled={!exchangeInput}
              >
                {t('points_exchange_button_exchange')}
              </button>
            </div>

            <div className={Styles.tips}>
              {t('points_exchange_rule_text')}{t('points_exchange_rule_text_rmb')}
              <span
                className={Styles.link}
                onClick={() => window.open('https://bbs.dji.com/pro/detail?tid=240733', '_blank')}
              >
                {t('points_exchange_learn_more')}
              </span>
            </div>

            {/* DJI Coins Row */}
            <div
              className={Styles.rowCard}
              onClick={() => {
                const url = process.env.DJI_STORE_CREDIT_URL;
                if (url) {
                  window.open(url, '_blank');
                } else {
                  Toast.show({ content: t('points_exchange_dji_coin_detail_not_configured') });
                }
              }}
            >
              <div className={Styles.rowTitle}>{t('points_exchange_my_dji_coin')}</div>
              <div className={Styles.rowRight}>
                <span className={Styles.rowAction}>{t('points_exchange_view_details')}</span>
                <GoDetailsIcon className={Styles.goDetailsIcon} />
              </div>
            </div>
            <div className={Styles.tips} style={{ textAlign: 'left', marginTop: '10px',textIndent:'10.5px' }}>
              {t('points_exchange_audit_arrival_tip')}
            </div>

            <div className={Styles.sectionTitle}>{t('points_exchange_records_title')}</div>
            <div className={Styles.recordList}>
              {records.length > 0 ? (
                records.map((item, idx) => {
                  const isLast = idx === records.length - 1;
                  const deltaText = item.delta >= 0
                    ? t('points_exchange_delta_positive', { value: item.delta })
                    : t('points_exchange_delta_negative', { value: item.delta });
                  return (
                    <div
                      key={`${item.title}-${item.date}-${idx}`}
                      className={`${Styles.recordItem} ${isLast ? Styles.recordItemLast : ""}`}
                    >
                      <div className={Styles.recordLeft}>
                        <div className={Styles.recordTitle}>
                          {item.title}
                          {item.status && item.title === '积分兑换' && <span className={`${Styles.statusTag} ${getStatusClass(item.status)}`}>{item.status}</span>}
                        </div>
                        <div className={Styles.recordDate}>{item.date}</div>
                      </div>
                      <div className={Styles.recordRight}>
                        <div className={Styles.recordDelta}>{deltaText}</div>
                        <div className={Styles.recordBalance}>{t('points_exchange_balance_label')}{item.balance}</div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '14px' }}>{t('points_exchange_no_records')}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Dialog Overlay - placed outside the modal container but inside the return fragment */}
      {confirmVisible && (
        <div className={Styles.confirmMask}>
          <div className={Styles.confirmDialog} onClick={e => e.stopPropagation()}>
            <div className={Styles.confirmTitle}>{t('points_exchange_confirm_title')}</div>
            <div className={Styles.confirmSubtitle}>{t('points_exchange_confirm_subtitle')}</div>
            
            {captchaConfig?.enabled && (
              <div className={Styles.captchaWrapper}>
                <AliyunCaptcha
                  sceneId={captchaSceneId}
                  prefix={captchaConfig.prefix}
                  onSuccess={onCaptchaSuccess}
                  onFail={onCaptchaFail}
                  width="100%"
                  height={48}
                  slideStyle={captchaSlideStyle}
                  getInstance={(instance) => {
                    captchaInstance.current = instance;
                  }}
                />
              </div>
            )}

            <div className={Styles.confirmActions}>
              <button
                className={Styles.cancelBtn}
                disabled={confirmSubmitting}
                onClick={() => setConfirmVisible(false)}
              >
                {t('points_exchange_cancel')}
              </button>
              <button
                className={Styles.confirmBtn}
                disabled={confirmSubmitting || (!!captchaConfig?.enabled && !isCaptchaVerified)}
                onClick={onConfirmExchange}
              >
                {confirmSubmitting ? t('points_exchange_submitting') : t('points_exchange_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// 导出组件。
export default PointsExchangeModal;
